import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import { sendVerificationCode } from '@/services/authService';
import { validateReferralCode } from '@/services/authService';
import ProgressIndicator from '@/components/ui/ProgressIndicator';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import BackgroundDecoration from '@/components/ui/BackgroundDecoration';
import TermsModal from '@/components/ui/TermsModal';
import { cn } from '@/utils/cn';
import { pageContainer, pageItem } from '@/utils/animations';
import { replaceCurrentPage } from '@/utils/navigationHistory';
import { analyticsService } from '@/services/analyticsService';

interface OnboardingPhoneProps {
  className?: string;
}

const OnboardingPhone = ({ className }: OnboardingPhoneProps) => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralMessage, setReferralMessage] = useState('');
  const [isReferralValid, setIsReferralValid] = useState(false);
  const [isApplyingReferral, setIsApplyingReferral] = useState(false);
  const sendInFlight = useRef(false);

  // جایگزینی صفحه فعلی در history تا کاربر نتواند به صفحات قبلی برگردد
  useEffect(() => {
    replaceCurrentPage('/onboarding/phone');
    const savedReferralCode = sessionStorage.getItem('onboarding_referral_code') || '';
    if (savedReferralCode) {
      setReferralCode(savedReferralCode);
      setReferralMessage('کد دعوت با موفقیت اعمال شد');
      setIsReferralValid(true);
    }
  }, []);

  // تبدیل اعداد انگلیسی به فارسی
  const toPersianDigits = (str: string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return str.replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
  };

  // تبدیل اعداد فارسی به انگلیسی
  const toEnglishDigits = (str: string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    let result = str;
    persianDigits.forEach((persianDigit, index) => {
      result = result.replace(new RegExp(persianDigit, 'g'), index.toString());
    });
    return result;
  };

  const validatePhoneNumber = (phone: string): { isValid: boolean; error?: string } => {
    const cleaned = phone.replace(/\s|-/g, '');
    
    if (!cleaned) {
      return { isValid: false };
    }
    
    if (!cleaned.startsWith('09')) {
      return { isValid: false, error: 'شماره موبایل باید با 09 شروع شود' };
    }
    
    if (cleaned.length !== 11) {
      return { isValid: false, error: 'شماره موبایل باید 11 رقم باشد' };
    }
    
    if (!/^\d+$/.test(cleaned)) {
      return { isValid: false, error: 'شماره موبایل فقط باید شامل اعداد باشد' };
    }
    
    return { isValid: true };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // تبدیل به انگلیسی و فقط اعداد را قبول کن
    const englishValue = toEnglishDigits(e.target.value);
    const numericValue = englishValue.replace(/[^0-9]/g, '');
    
    // ذخیره به صورت انگلیسی
    setPhoneNumber(numericValue);

    // Clear error when user starts typing
    if (error && numericValue.trim()) {
      setError('');
    }
  };

  const handleReferralInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value.toUpperCase().replace(/\s/g, '');
    setReferralCode(nextValue);

    if (isReferralValid) {
      setIsReferralValid(false);
    }

    if (referralMessage) {
      setReferralMessage('');
    }
  };

  const handleApplyReferral = async () => {
    const trimmedCode = referralCode.trim().toUpperCase();
    if (!trimmedCode) {
      setReferralMessage('کد معرفی رو وارد کن');
      setIsReferralValid(false);
      sessionStorage.removeItem('onboarding_referral_code');
      return;
    }

    setIsApplyingReferral(true);
    setReferralMessage('');

    try {
      const response = await validateReferralCode(trimmedCode);
      setReferralMessage(response.message);
      setIsReferralValid(response.is_valid);

      if (response.is_valid) {
        sessionStorage.setItem('onboarding_referral_code', trimmedCode);
      } else {
        sessionStorage.removeItem('onboarding_referral_code');
      }
    } catch (error) {
      setReferralMessage(error instanceof Error ? error.message : 'خطا در اعتبارسنجی کد معرفی');
      setIsReferralValid(false);
      sessionStorage.removeItem('onboarding_referral_code');
    } finally {
      setIsApplyingReferral(false);
    }
  };

  const handleContinue = async () => {
    const validation = validatePhoneNumber(phoneNumber);

    if (!validation.isValid) {
      setError(validation.error || 'لطفاً شماره موبایل معتبر وارد کنید');
      return;
    }

    if (sendInFlight.current) return;
    sendInFlight.current = true;

    setIsLoading(true);
    setError('');

    try {
      console.log('📱 [OnboardingPhone] Sending verification code to:', phoneNumber.trim());
      
      // Send verification code to phone
      const response = await sendVerificationCode(phoneNumber.trim());
      
      console.log('✅ [OnboardingPhone] Verification code sent, response:', response);
      void analyticsService.logEvent('onboarding_phone_submitted');
      
      // Update user with phone number
      sessionStorage.removeItem('onboarding_phone_verified');
      updateUser({
        phone_number: phoneNumber.trim()
      });
      
      // Save phone number to sessionStorage for refresh recovery
      sessionStorage.setItem('onboarding_phone_number', phoneNumber.trim());

      // Navigate to OTP verification (always registration flow in onboarding)
      console.log('📝 [OnboardingPhone] Navigating to onboarding OTP verification');
      navigate('/onboarding/otp-verification', {
        state: {
          phone_number: phoneNumber.trim()
        }
      });
    } catch (error) {
      console.error('❌ [OnboardingPhone] Error sending verification code:', error);
      setError(error instanceof Error ? error.message : 'خطا در ارسال کد تایید');
    } finally {
      sendInFlight.current = false;
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleContinue();
    }
  };

  return (
    <motion.div
      className={cn(
        'min-h-screen bg-white flex flex-col',
        'px-7 py-12',
        'relative overflow-hidden',
        className
      )}
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      dir="rtl"
    >
      {/* Progress indicator */}
      <motion.div
        className="mb-16"
        variants={pageItem}
      >
        <ProgressIndicator currentStep={1} totalSteps={4} />
      </motion.div>

      {/* Phone input section - centered */}
      <motion.div
        className="flex-1 flex flex-col justify-start pt-24 sm:pt-28 lg:pt-36 mb-12 lg:mb-16 max-w-4xl lg:mx-auto w-full"
        variants={pageItem}
      >
        <div className="space-y-6">
          <div className="w-full max-w-form-input mx-auto space-y-3">
            <motion.input
              type="tel"
              inputMode="numeric"
              value={toPersianDigits(phoneNumber)}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="شماره همراه"
              maxLength={11}
              className={cn(
                'w-full px-5 py-2.5 rounded-xl border-2 transition-all duration-300',
                'font-persian text-base text-foreground placeholder:text-muted',
                'focus:outline-none focus:ring-2 focus:ring-offset-1',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'text-center bg-soft-white shadow-sm',
                !error && [
                  'border-gray-200',
                  'hover:border-gray-300 hover:shadow-md',
                  'focus:border-gray-400 focus:ring-gray-200'
                ],
                error && [
                  'border-red-300 bg-red-50',
                  'hover:border-red-400',
                  'focus:border-red-500 focus:ring-red-200'
                ]
              )}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
            
            {error && (
              <motion.p
                className="text-red-500 text-sm font-persian text-center"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {error}
              </motion.p>
            )}
          </div>

          {/* Continue Button - below input */}
          <motion.div
            className="w-full max-w-form-input mx-auto mt-2"
            variants={pageItem}
          >
            <Button
              variant="primary"
              size="md"
              className="w-full rounded-xl"
              onClick={handleContinue}
              disabled={phoneNumber.length !== 11 || isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  <span>در حال ارسال...</span>
                </div>
              ) : (
                'ارسال کد تایید'
              )}
            </Button>
          </motion.div>

          <motion.div
            className="w-full max-w-form-input mx-auto"
            variants={pageItem}
          >
            <div className="relative overflow-hidden rounded-[24px] border border-white/80 bg-gradient-to-br from-white via-[#FFF8EE] to-[#F6EFE2] p-3.5 shadow-[0_16px_50px_-28px_rgba(149,109,44,0.34)]">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(244,205,118,0.22),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(227,198,146,0.16),transparent_38%)]" />

              <div className="relative space-y-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white/90 shadow-sm">
                      <span className="text-base">🎁</span>
                    </div>
                    <div className="text-right">
                      <p className="font-persian text-[13px] font-semibold text-foreground">
                        کد معرفی
                      </p>
                      <p className="font-persian text-[11px] leading-5 text-muted">
                        کد دعوت رو وارد کن و ۳ روز اشتراک رایگان هدیه بگیر !
                      </p>
                    </div>
                  </div>

                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={referralCode}
                        onChange={handleReferralInputChange}
                        placeholder="کد معرفی رو وارد کن"
                        maxLength={10}
                        dir="ltr"
                        className={cn(
                          'w-full rounded-2xl border px-4 py-2.5 text-center font-persian text-sm text-foreground transition-all duration-300',
                          'h-11 bg-white/90 placeholder:text-muted shadow-sm',
                          'focus:outline-none focus:ring-2 focus:ring-offset-1',
                          isReferralValid
                            ? 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-100'
                            : 'border-[#E8D8B5] hover:border-[#DABA79] focus:border-[#C79C47] focus:ring-[#F2DCA8]'
                        )}
                      />
                    </div>

                    <Button
                      variant={isReferralValid ? 'secondary' : 'primary'}
                      size="sm"
                      className={cn(
                        'h-11 shrink-0 self-stretch rounded-2xl px-4 py-0 leading-none',
                        isReferralValid && 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300'
                      )}
                      onClick={handleApplyReferral}
                      disabled={isApplyingReferral || referralCode.trim().length < 6}
                    >
                      {isApplyingReferral ? (
                        <div className="flex items-center gap-2">
                          <LoadingSpinner size="sm" />
                          <span>...</span>
                        </div>
                      ) : (
                        'اعمال'
                      )}
                    </Button>
                  </div>

                  {referralMessage && (
                    <motion.p
                      className={cn(
                        'px-1 text-right text-[11px] font-persian',
                        isReferralValid ? 'text-emerald-600' : 'text-red-500'
                      )}
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      {referralMessage}
                    </motion.p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Terms and conditions text */}
          <motion.div
            className="w-full text-center mt-6"
            variants={pageItem}
          >
            <p className="text-sm text-muted font-persian leading-relaxed px-4">
              با ثبت نام در بوکا{' '}
              <button
                onClick={() => setIsTermsModalOpen(true)}
                className="text-primary-500 hover:text-primary-600 underline transition-colors font-medium"
              >
                شرایط و قوانین
              </button>
              {' '}استفاده از خدمات را می‌پذیرم.
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Background decoration */}
      <BackgroundDecoration />
      
      {/* Terms Modal */}
      <TermsModal 
        isOpen={isTermsModalOpen} 
        onClose={() => setIsTermsModalOpen(false)} 
      />
    </motion.div>
  );
};

export default OnboardingPhone;
